import React from 'react'
import { DeviceCategory } from '@/types/devices'

export const CATEGORY_COLORS: Record<DeviceCategory, string> = {
  interface: '#3B82F6',
  mixer: '#F97316',
  synth: '#A855F7',
  drum_machine: '#EF4444',
  sampler: '#EC4899',
  controller: '#6366F1',
  effects: '#F59E0B',
  monitoring: '#22C55E',
  midi_interface: '#10B981',
  groovebox: '#F43F5E',
  semi_modular: '#8B5CF6',
  computer: '#64748B',
  usb_hub: '#0EA5E9',
  network_switch: '#06B6D4',
  recorder: '#D946EF',
  microphone: '#F43F5E',
}

export const CATEGORY_LABELS: Record<DeviceCategory, string> = {
  interface: 'Interface',
  mixer: 'Mixer',
  synth: 'Synth',
  drum_machine: 'Drums',
  sampler: 'Sampler',
  controller: 'Controller',
  effects: 'FX',
  monitoring: 'Monitor',
  midi_interface: 'MIDI I/F',
  groovebox: 'Groovebox',
  semi_modular: 'Semi-Mod',
  computer: 'Computer',
  usb_hub: 'USB Hub',
  network_switch: 'Network',
  recorder: 'Recorder',
  microphone: 'Microphone',
}

export const CategoryIcon: React.FC<{
  category: DeviceCategory
  size?: number
}> = ({ category, size = 32 }) => {
  const color = CATEGORY_COLORS[category]
  const s = size

  switch (category) {
    case 'interface':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="6"
            width="26"
            height="20"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="8"
            y1="12"
            x2="24"
            y2="12"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="8"
            y1="16"
            x2="24"
            y2="16"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="8"
            y1="20"
            x2="24"
            y2="20"
            stroke={color}
            strokeWidth="1.5"
          />
          <circle cx="10" cy="12" r="1.5" fill={color} />
          <circle cx="10" cy="16" r="1.5" fill={color} />
          <circle cx="10" cy="20" r="1.5" fill={color} />
        </svg>
      )
    case 'mixer':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="4"
            width="26"
            height="24"
            rx="2"
            stroke={color}
            strokeWidth="2"
          />
          <line x1="9" y1="8" x2="9" y2="24" stroke={color} strokeWidth="1.5" />
          <line
            x1="16"
            y1="8"
            x2="16"
            y2="24"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="23"
            y1="8"
            x2="23"
            y2="24"
            stroke={color}
            strokeWidth="1.5"
          />
          <rect x="7" y="11" width="4" height="3" rx="1" fill={color} />
          <rect x="14" y="15" width="4" height="3" rx="1" fill={color} />
          <rect x="21" y="9" width="4" height="3" rx="1" fill={color} />
        </svg>
      )
    case 'synth':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="8"
            width="26"
            height="16"
            rx="2"
            stroke={color}
            strokeWidth="2"
          />
          <line x1="9" y1="14" x2="9" y2="24" stroke={color} strokeWidth="1" />
          <line
            x1="15"
            y1="14"
            x2="15"
            y2="24"
            stroke={color}
            strokeWidth="1"
          />
          <line
            x1="21"
            y1="14"
            x2="21"
            y2="24"
            stroke={color}
            strokeWidth="1"
          />
          <line
            x1="27"
            y1="14"
            x2="27"
            y2="24"
            stroke={color}
            strokeWidth="1"
          />
          <rect x="7" y="8" width="3" height="9" rx="0.5" fill={color} />
          <rect x="13" y="8" width="3" height="9" rx="0.5" fill={color} />
          <rect x="20" y="8" width="3" height="9" rx="0.5" fill={color} />
        </svg>
      )
    case 'drum_machine':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="6"
            width="26"
            height="20"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <rect x="7" y="10" width="5" height="5" rx="1" fill={color} />
          <rect x="14" y="10" width="5" height="5" rx="1" fill={color} />
          <rect x="21" y="10" width="5" height="5" rx="1" fill={color} />
          <rect x="7" y="17" width="5" height="5" rx="1" fill={color} />
          <rect x="14" y="17" width="5" height="5" rx="1" fill={color} />
          <rect x="21" y="17" width="5" height="5" rx="1" fill={color} />
        </svg>
      )
    case 'sampler':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="6"
            width="26"
            height="20"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <polyline
            points="7,16 10,11 13,19 16,9 19,22 22,13 25,16"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      )
    case 'controller':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="6"
            width="26"
            height="20"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <circle cx="11" cy="14" r="3.5" stroke={color} strokeWidth="1.5" />
          <circle cx="21" cy="14" r="3.5" stroke={color} strokeWidth="1.5" />
          <line
            x1="11"
            y1="14"
            x2="11"
            y2="10.5"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="21"
            y1="14"
            x2="23"
            y2="11.5"
            stroke={color}
            strokeWidth="1.5"
          />
          <rect x="7" y="20" width="18" height="2" rx="1" fill={color} />
        </svg>
      )
    case 'effects':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="6"
            y="4"
            width="20"
            height="24"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <circle cx="16" cy="20" r="4" stroke={color} strokeWidth="2" />
          <circle cx="11" cy="10" r="2" stroke={color} strokeWidth="1" />
          <circle cx="16" cy="10" r="2" stroke={color} strokeWidth="1" />
          <circle cx="21" cy="10" r="2" stroke={color} strokeWidth="1" />
        </svg>
      )
    case 'monitoring':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="6"
            y="3"
            width="20"
            height="26"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <circle cx="16" cy="19" r="5" stroke={color} strokeWidth="2" />
          <circle cx="16" cy="19" r="1.5" fill={color} />
          <circle cx="16" cy="9" r="2.5" stroke={color} strokeWidth="1.5" />
        </svg>
      )
    case 'midi_interface':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="8"
            width="26"
            height="16"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <circle cx="12" cy="16" r="4" stroke={color} strokeWidth="1.5" />
          <circle cx="22" cy="16" r="4" stroke={color} strokeWidth="1.5" />
          <circle cx="12" cy="14" r="0.8" fill={color} />
          <circle cx="10.5" cy="17" r="0.8" fill={color} />
          <circle cx="13.5" cy="17" r="0.8" fill={color} />
          <circle cx="22" cy="14" r="0.8" fill={color} />
          <circle cx="20.5" cy="17" r="0.8" fill={color} />
          <circle cx="23.5" cy="17" r="0.8" fill={color} />
        </svg>
      )
    case 'groovebox':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="2"
            y="6"
            width="28"
            height="20"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <rect x="5" y="9" width="4" height="4" rx="1" fill={color} />
          <rect x="11" y="9" width="4" height="4" rx="1" fill={color} />
          <rect x="17" y="9" width="4" height="4" rx="1" fill={color} />
          <rect x="23" y="9" width="4" height="4" rx="1" fill={color} />
          <rect
            x="5"
            y="16"
            width="22"
            height="6"
            rx="1"
            stroke={color}
            strokeWidth="1"
          />
          <line
            x1="10"
            y1="16"
            x2="10"
            y2="22"
            stroke={color}
            strokeWidth="0.5"
          />
          <line
            x1="16"
            y1="16"
            x2="16"
            y2="22"
            stroke={color}
            strokeWidth="0.5"
          />
          <line
            x1="22"
            y1="16"
            x2="22"
            y2="22"
            stroke={color}
            strokeWidth="0.5"
          />
        </svg>
      )
    case 'semi_modular':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="4"
            width="26"
            height="24"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          {[8, 13, 18, 23].map(y =>
            [8, 13, 18, 23].map(x => (
              <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill={color} />
            )),
          )}
        </svg>
      )
    case 'computer':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="4"
            y="4"
            width="24"
            height="17"
            rx="2"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="12"
            y1="25"
            x2="20"
            y2="25"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="16"
            y1="21"
            x2="16"
            y2="25"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="8"
            y1="28"
            x2="24"
            y2="28"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )
    case 'usb_hub':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="5"
            y="10"
            width="22"
            height="12"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <circle cx="10" cy="16" r="1.5" fill={color} />
          <circle cx="16" cy="16" r="1.5" fill={color} />
          <circle cx="22" cy="16" r="1.5" fill={color} />
          <line
            x1="16"
            y1="6"
            x2="16"
            y2="10"
            stroke={color}
            strokeWidth="1.5"
          />
          <circle cx="16" cy="5" r="1.5" stroke={color} strokeWidth="1" />
        </svg>
      )
    case 'network_switch':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="10"
            width="26"
            height="12"
            rx="2"
            stroke={color}
            strokeWidth="2"
          />
          {[8, 13, 18, 23].map(x => (
            <rect
              key={x}
              x={x}
              y="14"
              width="3"
              height="4"
              rx="0.5"
              fill={color}
            />
          ))}
          <circle cx="7" cy="16" r="1" fill={color} />
        </svg>
      )
    case 'recorder':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="4"
            y="6"
            width="24"
            height="20"
            rx="3"
            stroke={color}
            strokeWidth="2"
          />
          <circle cx="12" cy="16" r="4" stroke={color} strokeWidth="1.5" />
          <circle cx="20" cy="16" r="4" stroke={color} strokeWidth="1.5" />
          <circle cx="12" cy="16" r="1" fill={color} />
          <circle cx="20" cy="16" r="1" fill={color} />
        </svg>
      )
    case 'microphone':
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="12"
            y="4"
            width="8"
            height="14"
            rx="4"
            stroke={color}
            strokeWidth="2"
          />
          <path
            d="M8 15c0 4.418 3.582 8 8 8s8-3.582 8-8"
            stroke={color}
            strokeWidth="2"
            fill="none"
          />
          <line
            x1="16"
            y1="23"
            x2="16"
            y2="28"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="11"
            y1="28"
            x2="21"
            y2="28"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )
    default:
      return (
        <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
          <rect
            x="3"
            y="6"
            width="26"
            height="20"
            rx="3"
            stroke="#999"
            strokeWidth="2"
          />
        </svg>
      )
  }
}
